import React, { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Colors } from '../../theme';
import { validatePassword } from 'shared';
import '@fontsource/work-sans';
import '@fontsource/pt-serif';
import IconButton from 'library/components/buttons/IconButton';

interface PasswordInputProps {
    id?: string;
    label: string;
    onChange: (value: string, isValid: boolean) => void;
    value: string;
    error?: boolean;
    errorText?: string;
    hideRequirements?: boolean;
}

const StyledInput = styled('input')({
    '&:hover': {
        outline: 'none',
    },
    '&:focus': {
        outline: 'none',
    },
    '&::placeholder': {
        color: Colors.mediumNavy,
    },
    borderBottomColor: Colors.mediumNavy,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    width: '100%',
    autoCapitalize: 'none',
});

const styles = {
    label: {
        fontFamily: 'Outfit',
        color: Colors.darkNavy,
        lineHeight: '1.2em',
        minHeight: '1.2em',
        display: 'block',
    },
    requirementsContainer: {
        marginTop: 4,
        minHeight: 80,
    },
    inputRow: {
        position: 'relative' as const,
    },
    toggleButton: {
        position: 'absolute' as const,
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        padding: 4,
    },
};

export default function PasswordInput(props: PasswordInputProps) {
    const { label, onChange, value, id, hideRequirements } = props;
    const [hidden, setHidden] = useState(true);
    const [focused, setFocused] = useState(false);

    const requirements = useMemo(() => {
        const lengthOk = value.length >= 8;
        const hasUppercase = /[A-Z]/.test(value);
        const hasLowercase = /[a-z]/.test(value);
        const hasDigit = /\d/.test(value);
        const hasSpecial = /[#?!@$%^&*-]/.test(value);

        return [
            {
                key: 'length',
                label: 'Minimum 8 characters',
                satisfied: lengthOk,
            },
            {
                key: 'uppercase',
                label: 'At least one uppercase letter',
                satisfied: hasUppercase,
            },
            {
                key: 'lowercase',
                label: 'At least one lowercase letter',
                satisfied: hasLowercase,
            },
            {
                key: 'digit',
                label: 'At least one digit',
                satisfied: hasDigit,
            },
            {
                key: 'special',
                label: 'At least one special character (#?!@$%^&*-)',
                satisfied: hasSpecial,
            },
        ];
    }, [value]);

    function getPlaceholder() {
        return value ? '' : label;
    }

    return (
        <Grid container direction="column">
            <Grid item>
                <label style={{ ...styles.label, visibility: value ? 'visible' : 'hidden' }}>{label}</label>
            </Grid>
            <Grid item style={styles.inputRow}>
                <StyledInput
                    id={id}
                    type={hidden ? 'password' : 'text'}
                    value={value}
                    onChange={(e) => onChange(e.target.value, validatePassword(e.target.value))}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder={getPlaceholder()}
                    style={{ color: Colors.darkNavy }}
                />
                <IconButton
                    style={styles.toggleButton}
                    onClick={() => setHidden(!hidden)}
                    size="small"
                    tabIndex={-1}
                    aria-label={hidden ? 'Show password' : 'Hide password'}
                >
                    {hidden ? (
                        <VisibilityOff style={{ fontSize: 18, color: Colors.grey }} />
                    ) : (
                        <Visibility style={{ fontSize: 18, color: Colors.darkNavy }} />
                    )}
                </IconButton>
            </Grid>
            {!hideRequirements && (
                <Grid item style={styles.requirementsContainer}>
                    {(focused || !!value) &&
                        requirements.map((req) => (
                            <Grid
                                key={req.key}
                                container
                                alignItems="center"
                                wrap="nowrap"
                                style={{ marginTop: 2 }}
                            >
                                <Grid item style={{ marginRight: 6 }}>
                                    {req.satisfied ? (
                                        <CheckCircleIcon style={{ fontSize: 18, color: Colors.turquoise }} />
                                    ) : (
                                        <CancelIcon style={{ fontSize: 18, color: Colors.mediumRed }} />
                                    )}
                                </Grid>
                                <Grid item>
                                    <span
                                        style={{
                                            fontFamily: 'Outfit',
                                            fontSize: 12,
                                            color: req.satisfied ? Colors.turquoise : Colors.mediumRed,
                                        }}
                                    >
                                        {req.label}
                                    </span>
                                </Grid>
                            </Grid>
                        ))}
                </Grid>
            )}
        </Grid>
    );
}

